import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getFirestore, doc, onSnapshot } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import Swal from 'sweetalert2';
import { useAuth } from '../context/AuthContext';
import { STRIPE_PUBLISHABLE_KEY } from '../firebaseConfig';
import Header from '../components/Header';
import Footer from '../components/Footer';
import './Reservations.css';

const db = getFirestore();
const functions = getFunctions();
const stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);

const STEPS = [
  { key: 'pending', label: 'Requested' },
  { key: 'confirmed', label: 'Confirmed' },
  { key: 'seated', label: 'Seated' },
  { key: 'completed', label: 'Done' },
];

const HERO_TEXT = {
  pending: 'Awaiting restaurant confirmation',
  confirmed: 'Confirmed',
  seated: 'Seated — enjoy!',
  completed: 'Completed',
  cancelled: 'Cancelled',
  no_show: 'Marked as no-show',
};

/** Card sheet for paying an UNPAID pre-order (Stripe Elements wrapper). */
const PayPreorderSheet = ({ reservation, reservationId, onDone }) => {
  const stripe = useStripe();
  const elements = useElements();
  const { userData } = useAuth();
  const [busy, setBusy] = useState(false);
  const total = reservation.preorderTotal || 0;

  const handlePay = async () => {
    if (!stripe) return;
    setBusy(true);
    try {
      const cardEl = elements.getElement(CardElement);
      const { error: pmError, paymentMethod: pm } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardEl,
        billing_details: { name: userData?.name || 'Guest' },
      });
      if (pmError) throw new Error(pmError.message || 'Invalid card details');

      const createPi = httpsCallable(functions, 'createPaymentIntent');
      const { data: piData } = await createPi({
        amount: Math.round(total * 100),
        currency: 'usd',
        businessId: reservation.businessId,
        stripeCustomerId: userData?.stripeCustomerId || null,
        source: 'public-web',
        applicationFeePercent: 5,
        locationId: reservation.locationId,
        paymentMethod: 'card',
        paymentMethodId: pm.id,
      });
      if (!piData.success) throw new Error(piData.error || 'Payment failed');

      let finalStatus = piData.status;
      if (finalStatus === 'requires_action') {
        const { paymentIntent: handled, error: actionError } = await stripe.handleCardAction(piData.clientSecret);
        if (actionError) throw new Error(actionError.message || 'Card authentication failed');
        finalStatus = handled.status;
      }
      if (finalStatus !== 'succeeded') throw new Error(`Payment status: ${finalStatus}`);

      // If a credit-card convenience fee applied, the backend reconciles
      // the totals server-side (see payReservationPreorder).
      const payFn = httpsCallable(functions, 'payReservationPreorder');
      await payFn({
        businessId: reservation.businessId,
        locationId: reservation.locationId,
        reservationId,
        stripePaymentIntentId: piData.paymentIntentId,
      });
      await Swal.fire({ icon: 'success', title: 'Paid!', text: 'Your pre-order is now prepaid.', confirmButtonColor: '#ff5722' });
      onDone();
    } catch (e) {
      Swal.fire('Error', e.message || 'Payment failed', 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ border: '1px solid #e5e7eb', borderRadius: 12, padding: 12, marginTop: 10 }}>
      <CardElement options={{ style: { base: { fontSize: '16px' } } }} />
      <button
        className="ff-resv-btn-confirm"
        style={{ width: '100%', padding: 12, marginTop: 10 }}
        disabled={busy}
        onClick={handlePay}
      >
        {busy ? 'Processing…' : `Pay $${total.toFixed(2)} now`}
      </button>
      <p className="ff-resv-note">Full refund if you cancel before being seated.</p>
    </div>
  );
};

/**
 * Reservation detail — live status, confirmation code, pre-order summary,
 * payment/refund state, confirm/cancel actions. Deep-link target for the
 * email/SMS confirmation links.
 */
const ReservationDetailPage = () => {
  const { reservationId } = useParams();
  const navigate = useNavigate();
  const { user, userCollection, loading: authLoading } = useAuth();
  const [reservation, setReservation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [showPaySheet, setShowPaySheet] = useState(false);

  useEffect(() => {
    if (authLoading || !user) return;
    const ref = doc(db, userCollection, user.uid, 'reservations', reservationId);
    const unsub = onSnapshot(ref, (snap) => {
      setReservation(snap.exists() ? { id: snap.id, ...snap.data() } : null);
      setLoading(false);
    }, (e) => {
      console.error('Error streaming reservation', e);
      setLoading(false);
    });
    return unsub;
  }, [user, userCollection, authLoading, reservationId]);

  const updateStatus = async (status) => {
    setBusy(true);
    try {
      const fn = httpsCallable(functions, 'updateReservationStatus');
      const { data } = await fn({
        businessId: reservation.businessId,
        locationId: reservation.locationId,
        reservationId,
        status,
      });
      if (data.refunded) {
        await Swal.fire({
          icon: 'success',
          title: 'Cancelled & refunded',
          text: 'Your card was fully refunded.',
          confirmButtonColor: '#ff5722',
        });
      }
    } catch (e) {
      Swal.fire('Error', e.message || 'Something went wrong', 'error');
    } finally {
      setBusy(false);
    }
  };

  const cancelWithConfirm = async () => {
    const prepaid = reservation.paymentStatus === 'prepaid';
    const result = await Swal.fire({
      title: 'Cancel reservation?',
      text: prepaid
        ? 'This reservation has a prepaid pre-order. Cancelling now will fully refund your card.'
        : 'Are you sure you want to cancel this reservation?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, cancel it',
      cancelButtonText: 'Keep it',
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
    });
    if (result.isConfirmed) await updateStatus('cancelled');
  };

  // ---------- Pre-order actions ----------

  const openMenuForPreorder = () => {
    const slug = (reservation.locationName || 'store')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '')
      .substring(0, 60);
    navigate(`/store/${slug}-${reservation.locationId}`, {
      state: {
        reservationContext: {
          reservationId,
          businessId: reservation.businessId,
          locationId: reservation.locationId,
          locationName: reservation.locationName,
          dateTime: reservation.dateTime?.toDate?.()?.toISOString() || null,
          partySize: reservation.partySize,
        },
      },
    });
  };

  const removeItemsWithRefund = async () => {
    const items = reservation.preorderItems || [];
    if (items.length === 0) return;
    const inputs = items.map((it, i) => `
      <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:8px;gap:8px;">
        <span style="flex:1;text-align:left;">${it.productName} ×${it.qty}</span>
        <input id="rm-${i}" type="number" min="0" max="${it.qty}" value="0"
          style="width:64px;padding:6px;border:1px solid #d1d5db;border-radius:8px;text-align:center;">
      </div>`).join('');
    const { value: removals } = await Swal.fire({
      title: 'Remove items (refund)',
      html: `
        <p style="font-size:12px;color:#6b7280;text-align:left;">
          Removed items are refunded to your card proportionally (item + tax + fees).
        </p>${inputs}`,
      showCancelButton: true,
      confirmButtonText: 'Refund selected',
      confirmButtonColor: '#ff5722',
      preConfirm: () => {
        const out = [];
        items.forEach((it, i) => {
          const qty = parseInt(document.getElementById(`rm-${i}`).value, 10) || 0;
          if (qty > 0) out.push({ productId: it.productId, qty });
        });
        return out.length > 0 ? out : false;
      },
    });
    if (!removals) return;
    setBusy(true);
    try {
      const fn = httpsCallable(functions, 'modifyReservationPreorder');
      const { data } = await fn({
        businessId: reservation.businessId,
        locationId: reservation.locationId,
        reservationId,
        removals,
      });
      await Swal.fire({
        icon: 'success',
        title: 'Refunded',
        text: `$${(data.refunded || 0).toFixed(2)} was refunded to your card.`,
        confirmButtonColor: '#ff5722',
      });
    } catch (e) {
      Swal.fire('Error', e.message || 'Something went wrong', 'error');
    } finally {
      setBusy(false);
    }
  };

  if (!authLoading && !user) {
    return (
      <>
        <Header />
        <div className="ff-resv-page">
          <div className="ff-resv-card" style={{ textAlign: 'center', padding: 40 }}>
            <i className="fas fa-lock" style={{ fontSize: 40, color: '#ff5722' }}></i>
            <h2>Sign in to view this reservation</h2>
            <p style={{ color: '#6b7280' }}>Use the same account you booked with.</p>
            <button className="ff-resv-submit" onClick={() => navigate('/explore')}>Back to explore</button>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const r = reservation;
  const dt = r?.dateTime?.toDate?.();
  const isTerminal = r && ['cancelled', 'no_show', 'completed'].includes(r.status);
  const stepIndex = r ? STEPS.findIndex((s) => s.key === r.status) : -1;
  const items = r?.preorderItems || [];

  return (
    <>
      <Header />
      <div className="ff-resv-page">
        {loading || authLoading ? (
          <p style={{ textAlign: 'center', color: '#6b7280' }}>Loading…</p>
        ) : !r ? (
          <div className="ff-resv-card" style={{ textAlign: 'center', padding: 40 }}>
            <p>Reservation not found.</p>
          </div>
        ) : (
          <>
            <div className={`ff-resv-hero ${r.status}`}>
              <i
                className={`fas ${
                  r.status === 'confirmed' ? 'fa-check-circle'
                  : r.status === 'pending' ? 'fa-hourglass-half'
                  : r.status === 'seated' ? 'fa-chair'
                  : r.status === 'cancelled' ? 'fa-times-circle'
                  : r.status === 'no_show' ? 'fa-user-slash'
                  : 'fa-utensils'
                }`}
                style={{ fontSize: 40, color: '#ff5722' }}
              ></i>
              <h2>{HERO_TEXT[r.status] || r.status}</h2>
              {['pending', 'confirmed'].includes(r.status) && (
                <>
                  <p style={{ color: '#6b7280', fontSize: 12, margin: '8px 0 0' }}>Show this code at arrival</p>
                  <div className="ff-resv-code">{r.confirmationNumber}</div>
                </>
              )}
            </div>

            {!isTerminal && stepIndex >= 0 && (
              <div className="ff-resv-steps">
                {STEPS.map((s, i) => (
                  <div key={s.key} className={`ff-resv-step ${i <= stepIndex ? 'done' : ''}`}>
                    <div className="dot"></div>
                    {s.label}
                  </div>
                ))}
              </div>
            )}

            <div className="ff-resv-card">
              <div className="ff-resv-detail-row">
                <i className="fas fa-utensils"></i>
                <span>{r.locationName}</span>
              </div>
              <div className="ff-resv-detail-row">
                <i className="fas fa-calendar"></i>
                <span>
                  {dt ? dt.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' }) : ''}
                  {' · '}
                  {dt ? dt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : ''}
                </span>
              </div>
              <div className="ff-resv-detail-row">
                <i className="fas fa-users"></i>
                <span>{r.partySize} guests</span>
              </div>
              {r.notes && (
                <div className="ff-resv-detail-row">
                  <i className="fas fa-sticky-note"></i>
                  <span>{r.notes}</span>
                </div>
              )}
              {r.tableName && (
                <div className="ff-resv-detail-row">
                  <i className="fas fa-chair"></i>
                  <span>Table: {r.tableName}</span>
                </div>
              )}
            </div>

            {items.length > 0 && (
              <div className="ff-resv-card">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                  <h3 className="ff-resv-section-title" style={{ margin: 0 }}>Pre-order</h3>
                  <span className={`ff-resv-status ${r.paymentStatus === 'refunded' ? 'cancelled' : r.paymentStatus === 'prepaid' ? 'confirmed' : 'pending'}`}>
                    {r.paymentStatus === 'refunded' ? 'Refunded' : r.paymentStatus === 'prepaid' ? 'Prepaid' : 'Pay at restaurant'}
                  </span>
                </div>
                {items.map((it, i) => (
                  <div key={i} className="ff-resv-total-row">
                    <span>{it.productName} × {it.qty}</span>
                    <span>${(it.total || 0).toFixed(2)}</span>
                  </div>
                ))}
                <div className="ff-resv-total-row bold" style={{ borderTop: '1px solid #f3f4f6', paddingTop: 8, marginTop: 4 }}>
                  <span>Total</span>
                  <span>${(r.preorderTotal || 0).toFixed(2)}</span>
                </div>
                {r.paymentStatus !== 'prepaid' && r.paymentStatus !== 'refunded' && !isTerminal && (
                  <p style={{ color: '#6b7280', fontSize: 12, marginTop: 8 }}>
                    Sent to the kitchen when you are seated. Pay at the restaurant.
                  </p>
                )}
              </div>
            )}

            {!isTerminal && (
              <>
                {/* Pre-order actions */}
                {!r.preorderSaleId ? (
                  <button className="ff-resv-submit" style={{ marginBottom: 10 }} onClick={openMenuForPreorder}>
                    <i className="fas fa-utensils" style={{ marginRight: 8 }}></i>
                    Add a pre-order from the menu
                  </button>
                ) : (
                  <>
                    {r.paymentStatus !== 'prepaid' && r.paymentStatus !== 'refunded' && (
                      <>
                        <button
                          className="ff-resv-btn-confirm"
                          style={{ width: '100%', padding: 14, marginBottom: 10 }}
                          disabled={busy}
                          onClick={() => setShowPaySheet(!showPaySheet)}
                        >
                          <i className="fas fa-credit-card" style={{ marginRight: 8 }}></i>
                          {showPaySheet ? 'Hide payment' : 'Pay pre-order now'}
                        </button>
                        {showPaySheet && (
                          <Elements stripe={stripePromise}>
                            <PayPreorderSheet
                              reservation={r}
                              reservationId={reservationId}
                              onDone={() => setShowPaySheet(false)}
                            />
                          </Elements>
                        )}
                        <button
                          className="ff-resv-btn-cancel"
                          style={{ width: '100%', padding: 12, marginBottom: 10, borderColor: '#ff5722', color: '#ff5722' }}
                          disabled={busy}
                          onClick={openMenuForPreorder}
                        >
                          <i className="fas fa-edit" style={{ marginRight: 8 }}></i>
                          Edit items
                        </button>
                      </>
                    )}
                    {r.paymentStatus === 'prepaid' && (
                      <button
                        className="ff-resv-btn-cancel"
                        style={{ width: '100%', padding: 12, marginBottom: 10 }}
                        disabled={busy}
                        onClick={removeItemsWithRefund}
                      >
                        {busy ? '…' : 'Remove items (refund)'}
                      </button>
                    )}
                  </>
                )}

                {r.status === 'pending' && (
                  <button className="ff-resv-btn-confirm" style={{ width: '100%', padding: 14, marginBottom: 10 }} disabled={busy} onClick={() => updateStatus('confirmed')}>
                    {busy ? '…' : 'Confirm reservation'}
                  </button>
                )}
                <button className="ff-resv-btn-cancel" style={{ width: '100%', padding: 14 }} disabled={busy} onClick={cancelWithConfirm}>
                  {busy ? '…' : r.paymentStatus === 'prepaid' ? 'Cancel & refund' : 'Cancel reservation'}
                </button>
                {r.paymentStatus === 'prepaid' && (
                  <p className="ff-resv-note">Free cancellation with a full refund any time before you are seated.</p>
                )}
              </>
            )}
          </>
        )}
      </div>
      <Footer />
    </>
  );
};

export default ReservationDetailPage;
